import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Download, Truck, PackageCheck, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return String(d).slice(0, 10); }
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return dt.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return String(d); }
};

/**
 * Affiche les bons de chargement/déchargement SIGNÉS d'une opération
 * dans l'espace client. Téléchargement PDF possible si bon signé.
 *
 * PM Spec 30/04/2026
 */
const ClientLoadingNotesSection = ({ operationId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = () => 'cookie-auth';

  const fetchNotes = useCallback(async () => {
    if (!operationId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/client/operations/${operationId}/loading-notes`, {
      });
      setNotes(r.data?.loadingNotes || []);
    } catch {
      // silencieux : si 404 ou pas d'access, on n'affiche juste rien
      setNotes([]);
    } finally { setLoading(false); }
  }, [operationId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleDownload = async (noteId, noteNumber) => {
    try {
      const r = await axios.get(
        `${API}/client/operations/${operationId}/loading-notes/${noteId}/pdf`,
        {
          responseType: 'blob',
        }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${noteNumber}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'PDF indisponible';
      toast({
        title: 'Téléchargement impossible',
        description: typeof detail === 'string' ? detail : 'PDF indisponible',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-zinc-400 py-3 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement bons signés…
      </div>
    );
  }

  if (!notes.length) return null;

  const signedNotes = notes.filter(n => n.isSigned);

  return (
    <div className="space-y-3" data-testid="client-loading-notes-section">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-emerald-600" />
        <h3 className="text-sm font-semibold text-gray-700">
          Bons de chargement & déchargement signés
        </h3>
      </div>
      {signedNotes.length === 0 && (
        <p className="text-xs text-zinc-400 italic">
          Les bons sont en cours de signature côté terrain.
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {notes.map(n => {
          const isLoading = n.type === 'loading';
          const Icon = isLoading ? Truck : PackageCheck;
          const label = isLoading ? 'Bon de chargement' : 'Bon de déchargement';
          return (
            <Card key={n.id} className={n.isSigned ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/20'}
                  data-testid={`client-note-${n.type}`}>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-emerald-700" />
                    <div>
                      <div className="text-sm font-medium text-gray-800">{label}</div>
                      <div className="text-[11px] font-mono text-gray-500">{n.noteNumber}</div>
                    </div>
                  </div>
                  {n.isSigned ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">
                      Signé
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px]">
                      {n.signaturesCount}/2 signatures
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-gray-600 grid grid-cols-2 gap-x-3 gap-y-0.5">
                  <div>Date : <strong>{fmtDate(n.actualDate)}</strong></div>
                  <div>Fin : <strong>{n.endTime || '—'}</strong></div>
                  {n.actualWeight != null && <div>Poids : <strong>{n.actualWeight} T</strong></div>}
                  {n.actualState && <div>État : <strong>{n.actualState}</strong></div>}
                </div>
                {n.signers?.length > 0 && (
                  <div className="text-[11px] text-gray-500 space-y-0.5 pt-1 border-t border-emerald-100">
                    {n.signers.map((s, i) => (
                      <div key={i}>
                        {s.party === 'driver' ? '🚛' : (s.party === 'shipper' ? '📤' : '📥')}
                        {' '}{s.name} <span className="text-gray-400">— {fmtDateTime(s.signedAt)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {n.isSigned && (
                  <Button size="sm" variant="outline"
                          onClick={() => handleDownload(n.id, n.noteNumber)}
                          className="w-full h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                          data-testid={`client-download-${n.type}`}>
                    <Download className="w-3 h-3 mr-1" /> Télécharger le PDF signé
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ClientLoadingNotesSection;
